import type { SubmissionListParams } from '../adminApi'
import type { SubmissionItem } from '../types'
import { bugStatusLabel, suggestionStatusLabel } from './labels'

export type FeedType = '' | 'bug' | 'feedback' | 'rating'

export interface StatusFilters {
  type: FeedType
  q: string
  page: number
}

export const DEFAULT_STATUS_FILTERS: StatusFilters = { type: '', q: '', page: 1 }
export const STATUS_PAGE_SIZE = 50

const TYPES: FeedType[] = ['bug', 'feedback', 'rating']

export function statusFiltersFromSearch(search: string | URLSearchParams): StatusFilters {
  const p = typeof search === 'string' ? new URLSearchParams(search) : search
  const type = (p.get('type') ?? '') as FeedType
  const page = Number.parseInt(p.get('page') ?? '', 10)
  return {
    type: TYPES.includes(type) ? type : '',
    q: p.get('q') ?? '',
    page: Number.isFinite(page) && page >= 1 ? page : 1,
  }
}

export function searchFromStatusFilters(f: StatusFilters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.type) p.set('type', f.type)
  if (f.q) p.set('q', f.q)
  if (f.page > 1) p.set('page', String(f.page))
  return p
}

export function toStatusListParams(f: StatusFilters): SubmissionListParams {
  return { type: f.type || undefined, q: f.q || undefined, page: f.page, page_size: STATUS_PAGE_SIZE }
}

/** Human label for the submission kind, using the sub-form for suggestions. */
export function kindLabel(item: Pick<SubmissionItem, 'type' | 'subtype'>): string {
  if (item.type === 'bug') return 'Bug'
  if (item.type === 'rating') return 'Rating'
  return item.subtype === 'new_feature' ? 'New feature' : 'Improvement'
}

/** The team-side status as a label, per kind. Ratings have no lifecycle. */
export function adminStatusLabel(item: Pick<SubmissionItem, 'type' | 'admin_status'>): string {
  if (item.type === 'rating' || !item.admin_status) return '—'
  return item.type === 'bug' ? bugStatusLabel(item.admin_status) : suggestionStatusLabel(item.admin_status)
}

/** Where the dedicated tab for this row lives, when one exists. */
export function detailPath(item: Pick<SubmissionItem, 'type' | 'id'>): string | null {
  if (item.type === 'bug') return `/admin/bugs/${item.id}`
  if (item.type === 'feedback') return '/admin/suggestions'
  return null
}

export function pageCount(total: number, pageSize = STATUS_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize))
}
